import React, { useEffect, useState, useCallback } from "react";
import { fetchPermissionChanges } from "../api/client";
import {
  PermissionSnapshot,
  PermissionUser,
  PermissionRoleChange,
} from "../types";
import StatCard from "../components/StatCard";
import ShowPersonalToggle from "../components/ShowPersonalToggle";
import useWorkspaceList from "../hooks/useWorkspaceList";
import { useDateFormat } from "../preferences/PreferencesProvider";
import {
  calendarDateIn,
  formatDateTime,
  parseInstant,
  utcDaysAgo,
} from "../utils/datetime";

function ArtifactTypeBadge({ type }: { type?: string }) {
  const t = (type || "").toLowerCase();
  const cls =
    t === "report"
      ? "bg-blue-100 text-blue-700 border-blue-200"
      : t === "dataset"
      ? "bg-purple-100 text-purple-700 border-purple-200"
      : t === "dashboard"
      ? "bg-teal-100 text-teal-700 border-teal-200"
      : t === "dataflow"
      ? "bg-indigo-100 text-indigo-700 border-indigo-200"
      : t === "workspace"
      ? "bg-gray-100 text-gray-600 border-gray-200"
      : "bg-gray-100 text-gray-500 border-gray-200";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border capitalize ${cls}`}>
      {type || "unknown"}
    </span>
  );
}

function UserChip({ user, kind }: { user: PermissionUser; kind: "added" | "removed" }) {
  const cls =
    kind === "added"
      ? "bg-green-50 text-green-800 border-green-200"
      : "bg-red-50 text-red-800 border-red-200";
  const sign = kind === "added" ? "+" : "−";
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs border ${cls}`}
      title={user.displayName || user.email}
    >
      <span className="font-bold">{sign}</span>
      <span className="font-medium">{user.email || user.displayName || "unknown"}</span>
      {user.role && <span className="opacity-70">({user.role})</span>}
    </span>
  );
}

function RoleChangeChip({ change }: { change: PermissionRoleChange }) {
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs border bg-yellow-50 text-yellow-800 border-yellow-200"
      title={change.displayName || change.email}
    >
      <span className="font-medium">{change.email || change.displayName || "unknown"}</span>
      <span className="opacity-70">{change.previousRole || "none"}</span>
      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
      </svg>
      <span className="font-semibold">{change.newRole || "none"}</span>
    </span>
  );
}

function ChangeSection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-start gap-1.5">
      <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider w-24 shrink-0 pt-1.5">
        {label}
      </span>
      <div className="flex flex-wrap gap-1.5">{children}</div>
    </div>
  );
}

export default function AccessChangesPage() {
  const dateFormat = useDateFormat();
  const formatDate = (value?: string) => formatDateTime(value, dateFormat);

  const [snapshots, setSnapshots] = useState<PermissionSnapshot[]>([]);
  const { workspaces, loading: wsLoading, showPersonal, setShowPersonal } = useWorkspaceList({ monitoredOnly: true });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // SOX review default: last 3 months to today
  const [dateFrom, setDateFrom] = useState(utcDaysAgo(90));
  const [dateTo, setDateTo] = useState("");
  const [workspaceId, setWorkspaceId] = useState("");

  const fetchData = useCallback(() => {
    setLoading(true);
    setError(null);
    const params: Record<string, string> = {};
    if (dateFrom) params.date_from = dateFrom;
    if (workspaceId) params.workspace_id = workspaceId;
    // Without this, picking a personal workspace from the widened dropdown
    // returned nothing — the endpoint scopes to monitored workspaces — and an
    // empty access-changes table reads as "no access changed in there".
    if (showPersonal) params.monitored_only = "false";

    fetchPermissionChanges(params)
      .then((data) => setSnapshots(data))
      .catch((err) => setError(err.message ?? "Failed to load access changes."))
      .finally(() => setLoading(false));
  }, [dateFrom, workspaceId, showPersonal]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPersonal]);

  // The API supports date_from; apply the upper bound client-side.
  //
  // Both sides of this comparison have to be UTC calendar dates. `dateTo` is
  // one because the picker is labelled UTC and the lower bound it pairs with
  // goes to the API as a UTC partition key. `snapshotAt` is derived rather
  // than sliced: the string is normally already +00:00, but slicing the first
  // ten characters of a timestamp carrying any other offset silently yields
  // that zone's date instead, and a row dropped by an off-by-one upper bound
  // leaves no trace — the table just shows fewer access changes than
  // happened.
  const filtered = dateTo
    ? snapshots.filter((s) => {
        const instant = parseInstant(s.snapshotAt);
        // An unparsable timestamp is not evidence that the row falls outside
        // the window, so it is kept rather than quietly discarded.
        return !instant || calendarDateIn("UTC", instant) <= dateTo;
      })
    : snapshots;

  const totals = filtered.reduce(
    (acc, s) => {
      acc.added += s.changes?.added?.length ?? 0;
      acc.removed += s.changes?.removed?.length ?? 0;
      acc.roleChanged += s.changes?.roleChanged?.length ?? 0;
      return acc;
    },
    { added: 0, removed: 0, roleChanged: 0 }
  );

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Access Changes</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            SOX access-review feed — who gained, lost, or changed access on monitored Power BI artifacts.
          </p>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Change Events" value={filtered.length} severity="normal" />
        <StatCard label="Users Added" value={totals.added} severity={totals.added > 0 ? "warning" : "normal"} />
        <StatCard label="Users Removed" value={totals.removed} severity="normal" />
        <StatCard label="Role Changes" value={totals.roleChanged} severity={totals.roleChanged > 0 ? "warning" : "normal"} />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-500">From (UTC)</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-500">To (UTC)</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-500">Workspace</label>
          <select
            value={workspaceId}
            onChange={(e) => setWorkspaceId(e.target.value)}
            disabled={wsLoading}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white w-64 disabled:text-gray-400"
          >
            <option value="">{wsLoading ? "Loading workspaces…" : "All workspaces"}</option>
            {workspaces
              .slice()
              .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
              .map((ws) => (
                <option key={ws.workspaceId} value={ws.workspaceId}>
                  {ws.name || ws.workspaceId}
                </option>
              ))}
          </select>
        </div>
        <button
          onClick={fetchData}
          className="px-4 py-1.5 bg-blue-900 text-white text-sm font-medium rounded-lg hover:bg-blue-800 transition-colors"
        >
          Apply
        </button>
        <div className="pb-1.5">
          <ShowPersonalToggle show={showPersonal} onChange={setShowPersonal} />
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <svg className="w-6 h-6 animate-spin mr-2" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
          Loading access changes…
        </div>
      )}

      {error && !loading && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 flex items-center justify-between">
          <span>{error}</span>
          <button onClick={fetchData} className="ml-4 text-red-600 underline text-xs">Retry</button>
        </div>
      )}

      {!loading && !error && filtered.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-gray-400 gap-3">
          <p className="text-lg font-semibold">No access changes found</p>
          <p className="text-sm">Permission snapshots with no diff against the previous run are not shown.</p>
        </div>
      )}

      {!loading && !error && filtered.length > 0 && (
        <div className="space-y-3">
          {filtered.map((s) => (
            <div
              key={`${s.artifactId}-${s.snapshotAt}`}
              className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm space-y-3"
            >
              <div className="flex items-center flex-wrap gap-x-3 gap-y-1">
                <span className="text-sm font-medium text-gray-500 whitespace-nowrap">
                  {formatDate(s.snapshotAt)}
                </span>
                <ArtifactTypeBadge type={s.artifactType} />
                <span className="text-sm font-semibold text-gray-800 truncate" title={s.artifactName}>
                  {s.artifactName || s.artifactId}
                </span>
                <span className="text-xs text-gray-400 truncate" title={s.workspaceName}>
                  in {s.workspaceName || s.workspaceId || "unknown workspace"}
                </span>
              </div>

              <div className="space-y-2">
                {(s.changes?.added?.length ?? 0) > 0 && (
                  <ChangeSection label="Added">
                    {s.changes!.added.map((u, i) => (
                      <UserChip key={`${u.email}-${i}`} user={u} kind="added" />
                    ))}
                  </ChangeSection>
                )}
                {(s.changes?.removed?.length ?? 0) > 0 && (
                  <ChangeSection label="Removed">
                    {s.changes!.removed.map((u, i) => (
                      <UserChip key={`${u.email}-${i}`} user={u} kind="removed" />
                    ))}
                  </ChangeSection>
                )}
                {(s.changes?.roleChanged?.length ?? 0) > 0 && (
                  <ChangeSection label="Role">
                    {s.changes!.roleChanged.map((c, i) => (
                      <RoleChangeChip key={`${c.email}-${i}`} change={c} />
                    ))}
                  </ChangeSection>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
